/**
 * Partner API keys — generation, hashing and the Firestore document shape.
 *
 * This MUST stay byte-compatible with `api/partner/keys.py` in newgate/indexer,
 * which is what validates the keys this panel mints:
 *
 *   - 43 characters from a 62-character alphabet, so 256 bits of entropy
 *   - prefixed `ng_live_` (`ng_test_` keys exist and validate, but nothing
 *     mints them any more); the first 12 characters are the log-safe handle
 *     and the only part of a key allowed to appear in a log line or a metric
 *     label
 *   - the SHA-256 hex of the raw key IS the document id, so validation is a
 *     direct get() rather than a query
 *
 * The raw key is shown once and never stored. There is deliberately no way to
 * recover it: if it is lost, revoke and issue another.
 *
 * The key DOCUMENT is written server-side by functions/admin/adminCreateApiKey.js
 * -- it owns the authorization fields, so it owns the shape. The field set is
 * pinned there, in adminApiKeys.test.js. This module keeps only what the client
 * genuinely owns: generating the secret, hashing it, and the eligibility rule.
 *
 * The parity test in apiKeys.test.ts pins the values the Python side produces.
 */

const ALPHABET =
    'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

export const PREFIX_LEN = 12;
const BODY_LEN = 43;
const KEY_PREFIX = 'ng_live_';

/**
 * A new raw key.
 *
 * `crypto.getRandomValues`, never `Math.random`. Rejection sampling rather
 * than `% 62`: 256 does not divide evenly by 62, so the modulo would make the
 * first eight letters of the alphabet ~1.6% likelier than the rest. That is a
 * small bias, but it is a bias in a credential, and avoiding it costs nothing.
 */
export function generateKey(): string {
    const limit = Math.floor(256 / ALPHABET.length) * ALPHABET.length; // 248
    const out: string[] = [];
    const buf = new Uint8Array(BODY_LEN * 2);

    while (out.length < BODY_LEN) {
        crypto.getRandomValues(buf);
        for (const byte of buf) {
            if (byte < limit) {
                out.push(ALPHABET[byte % ALPHABET.length]);
                if (out.length === BODY_LEN) break;
            }
        }
    }
    return `${KEY_PREFIX}${out.join('')}`;
}

/** The log-safe handle: `ng_live_7f3a`. */
export function keyPrefix(raw: string): string {
    return raw.slice(0, PREFIX_LEN);
}

/** SHA-256 hex — the Firestore document id. Matches hashlib.sha256 in Python. */
export async function hashKey(raw: string): Promise<string> {
    const digest = await crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(raw)
    );
    return Array.from(new Uint8Array(digest))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
}

/**
 * A fresh key together with the two values the server stores of it. The raw
 * key leaves this function only into the caller's hands, which show it once.
 */
export async function newKey(): Promise<{
    raw: string;
    key_hash: string;
    prefix: string;
}> {
    const raw = generateKey();
    return { raw, key_hash: await hashKey(raw), prefix: keyPrefix(raw) };
}

/**
 * Whether a user may hold a key at all: approved, and either an admin or
 * granted the `api` feature. Mirrors the check in adminCreateApiKey, which
 * adds `api` to an admin key's feature copy so `_eligible` in keys.py passes.
 */
export function canHoldKey(user: {
    role?: string;
    status?: string;
    allowed_features?: unknown;
}): boolean {
    if (user.status !== 'approved') return false;
    if (user.role === 'admin') return true;
    return (
        Array.isArray(user.allowed_features) &&
        user.allowed_features.includes('api')
    );
}

/**
 * Why a user cannot get another key: adminCreateApiKey allows one active key
 * per user and refuses the rest with `active_key_exists`.
 */
export const ACTIVE_KEY_EXISTS =
    'This user already has an active key. Revoke it first to issue a new one.';
